import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, ChevronRight, Download } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SortableTable, type SortableColumn } from '@/components/ui/sortable-table';
import { StatTile } from '@/components/ui/stat-tile';
import { useBusinesses } from '@/features/business/use-businesses';
import {
  ALL_CHANNELS,
  CHANNELS,
  type SummaryChannel,
} from '@/features/sales-summary/sales-summary.api';
import { saveAsFile } from '@/lib/download';
import { formatCurrency, formatNumber, formatStoreDateTime } from '@/lib/format';
import { storeToday } from '@/lib/store-time';
import {
  GROUPINGS,
  useSalesOrders,
  useSalesReport,
  type SalesGrouping,
  type SalesOrder,
  type SalesQuery,
  type SalesRow,
} from './sales-report.api';

/**
 * Sales — one page for the legacy General, In Store, In-Store Employees and By
 * Station reports.
 *
 * Those were the same measures grouped four ways, under four sets of headers
 * that never matched the dashboard. Here the grouping is a filter, the columns
 * are the dashboard's own measures, the tiles are the dashboard's figures, and
 * the rows add up to the tiles. A row opens its orders, found with the same
 * grouping key, so they add up to the row.
 */

const ANY = 'ANY';
const ORDERS_PAGE_SIZE = 50;

/**
 * Grouping and channel from `?groupBy=` and `?channel=`, read once.
 *
 * The three legacy paths this page replaces redirect here with the grouping —
 * and the POS-only channel — they used to show.
 */
function fromUrl(): { groupBy: SalesGrouping; channel: SummaryChannel } {
  const params = new URLSearchParams(window.location.search);
  const groupBy = params.get('groupBy');
  const channel = params.get('channel');
  return {
    groupBy: GROUPINGS.includes(groupBy as SalesGrouping)
      ? (groupBy as SalesGrouping)
      : 'day',
    channel: CHANNELS.find((entry) => entry.key === channel)?.value ?? ALL_CHANNELS,
  };
}

/** A week back from `day`, inclusive of both ends. */
function weekEnding(day: string) {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 6);
  return date.toISOString().slice(0, 10);
}

export function SalesReportPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [initial] = useState(fromUrl);
  const today = storeToday();
  const [start, setStart] = useState(() => weekEnding(today));
  const [end, setEnd] = useState(today);
  const [businessId, setBusinessId] = useState(ANY);
  const [channel, setChannel] = useState<SummaryChannel>(initial.channel);
  const [groupBy, setGroupBy] = useState<SalesGrouping>(initial.groupBy);

  const [selected, setSelected] = useState<SalesRow | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [ordersPage, setOrdersPage] = useState(0);

  const { data: businesses } = useBusinesses();

  const query: SalesQuery = {
    start,
    end,
    businessId: businessId === ANY ? null : businessId,
    channel,
    groupBy,
  };
  const report = useSalesReport(query);
  const orders = useSalesOrders(
    query,
    sheetOpen && selected ? selected.key : null,
    ordersPage + 1,
    ORDERS_PAGE_SIZE
  );

  const rows = report.data?.rows ?? [];
  const totals = report.data?.totals;
  const loading = report.isPending;
  const busy = report.isFetching;

  /** Any filter change can remove the row whose orders are open. */
  function change<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setSheetOpen(false);
    };
  }

  const channelKey = CHANNELS.find((entry) => entry.value === channel)?.key ?? 'all';

  /** A dashboard tooltip, plus when this figure is that tile's figure. */
  function hint(help: string, tile: string) {
    const note =
      channel === ALL_CHANNELS
        ? t('salesSummary.sameTile', { tile })
        : t('salesSummary.channelTile', {
            tile,
            channel: t(`salesSummary.channel_${channelKey}`),
          });
    return `${help} ${note}`;
  }

  function rowLabel(row: SalesRow) {
    if (row.key === 'none') {
      return groupBy === 'employee'
        ? t('salesReport.noEmployee')
        : t('salesReport.noLane');
    }
    if (groupBy === 'day' && row.key) {
      const weekday = new Date(`${row.key}T00:00:00.000Z`).toLocaleDateString(
        language,
        { weekday: 'short', timeZone: 'UTC' }
      );
      return `${row.key} · ${weekday}`;
    }
    return row.label?.trim() || t('salesReport.unnamed');
  }

  const fields: FilterField[] = [
    {
      id: 'groupBy',
      label: t('salesReport.groupBy'),
      value: groupBy,
      resetValue: 'day',
      sort: false,
      onChange: change((value: string) => setGroupBy(value as SalesGrouping)),
      options: GROUPINGS.map((value) => ({
        value,
        label: t(`salesReport.groupBy_${value}`),
      })),
    },
    {
      id: 'store',
      label: t('salesReport.store'),
      value: businessId,
      onChange: change(setBusinessId),
      options: [
        { value: ANY, label: t('salesReport.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'channel',
      label: t('salesSummary.channel'),
      value: String(channel),
      resetValue: String(ALL_CHANNELS),
      sort: false,
      onChange: change((value: string) => setChannel(Number(value) as SummaryChannel)),
      options: CHANNELS.map((entry) => ({
        value: String(entry.value),
        label: t(`salesSummary.channel_${entry.key}`),
      })),
    },
    {
      id: 'start',
      label: t('salesReport.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: change(setStart),
    },
    {
      id: 'end',
      label: t('salesReport.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: change(setEnd),
    },
  ];

  function openRow(row: SalesRow) {
    setSelected(row);
    setOrdersPage(0);
    setSheetOpen(true);
  }

  // ---------------------------------------------------------------- export

  function runExport() {
    if (!rows.length) {
      toast.info(t('salesReport.nothingToExport'));
      return;
    }
    const escape = (value: unknown) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      [
        t(`salesReport.groupBy_${groupBy}`),
        t('dashboard.orders'),
        t('dashboard.grossSales'),
        t('salesReport.discounts'),
        t('salesReport.tax'),
        t('dashboard.amountCollected'),
        t('salesReport.refunds'),
        t('dashboard.netSales'),
        t('dashboard.giftCardRedemptions'),
        t('dashboard.averageTicket'),
      ].join(','),
      ...rows.map((row) =>
        [
          groupBy === 'day' ? row.key : rowLabel(row),
          row.orders,
          row.grossSales,
          row.discounts,
          row.tax,
          row.amountCollected,
          row.refunds,
          row.netSales,
          row.giftCardRedemptions,
          row.averageTicket,
        ]
          .map(escape)
          .join(',')
      ),
    ].join('\n');
    saveAsFile(
      csv,
      `sales-by-${groupBy}-${start}-to-${end}.csv`,
      'text/csv;charset=utf-8'
    );
    toast.success(t('salesReport.exported', { rows: rows.length }));
  }

  // --------------------------------------------------------------- columns

  const money = (
    key: string,
    header: string,
    pick: (row: SalesRow) => number,
    dashWhenZero = false
  ): SortableColumn<SalesRow> => ({
    key,
    header,
    align: 'right',
    sortValue: pick,
    render: (row) =>
      dashWhenZero && !pick(row) ? '—' : formatCurrency(pick(row)),
  });

  const columns: SortableColumn<SalesRow>[] = [
    {
      key: 'group',
      header: t(`salesReport.groupBy_${groupBy}`),
      sortValue: (row) => (groupBy === 'day' ? (row.key ?? '') : rowLabel(row)),
      render: (row) => (
        <span className="flex items-center gap-1">
          <span className="min-w-0">
            <span className="block font-medium whitespace-nowrap">
              {rowLabel(row)}
            </span>
            {row.detail && (
              <span className="text-muted-foreground block text-xs">
                {row.detail}
              </span>
            )}
          </span>
          <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
        </span>
      ),
    },
    {
      key: 'orders',
      header: t('dashboard.orders'),
      align: 'right',
      sortValue: (row) => row.orders,
      render: (row) => formatNumber(row.orders),
    },
    money('grossSales', t('dashboard.grossSales'), (row) => row.grossSales),
    money('discounts', t('salesReport.discounts'), (row) => row.discounts, true),
    money('tax', t('salesReport.tax'), (row) => row.tax),
    money('collected', t('dashboard.amountCollected'), (row) => row.amountCollected),
    money('refunds', t('salesReport.refunds'), (row) => row.refunds, true),
    money('netSales', t('dashboard.netSales'), (row) => row.netSales),
    money(
      'giftCards',
      t('dashboard.giftCardRedemptions'),
      (row) => row.giftCardRedemptions,
      true
    ),
    money('averageTicket', t('dashboard.averageTicket'), (row) => row.averageTicket),
  ];

  const tile = { loading, busy };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('salesReport.title')}
        description={t('salesReport.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={runExport}
          >
            <Download className="size-3.5" />
            <span className="hidden sm:inline">{t('salesReport.export')}</span>
          </Button>
        }
      />

      {/* Five controls in a row of four would leave "To" on a line of its own,
          apart from "From". */}
      <FilterBar busy={busy} fields={fields} columns={5} />

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatTile
          label={t('dashboard.orders')}
          value={formatNumber(totals?.orders ?? 0)}
          tooltip={hint(t('dashboard.ordersHelp'), t('dashboard.orders'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.grossSales')}
          value={formatCurrency(totals?.grossSales ?? 0)}
          tooltip={hint(t('dashboard.grossSalesHelp'), t('dashboard.grossSales'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.netSales')}
          value={formatCurrency(totals?.netSales ?? 0)}
          tone="primary"
          tooltip={hint(t('dashboard.netSalesHelp'), t('dashboard.netSales'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.amountCollected')}
          value={formatCurrency(totals?.amountCollected ?? 0)}
          tone="cash"
          tooltip={hint(
            t('dashboard.amountCollectedHelp'),
            t('dashboard.amountCollected')
          )}
          {...tile}
        />
        <StatTile
          label={t('salesReport.refunds')}
          value={formatCurrency(totals?.refunds ?? 0)}
          tooltip={t('salesReport.refundsHint')}
          {...tile}
        />
        <StatTile
          label={t('dashboard.averageTicket')}
          value={formatCurrency(totals?.averageTicket ?? 0)}
          tooltip={hint(
            t('dashboard.averageTicketHelp'),
            t('dashboard.averageTicket')
          )}
          {...tile}
        />
      </div>

      {report.error && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {report.error instanceof Error
              ? report.error.message
              : t('salesReport.failed')}
          </span>
        </div>
      )}

      <section className="space-y-2">
        <SortableTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.key ?? 'none'}
          loading={loading}
          busy={busy}
          pageSize={50}
          initialSort={
            groupBy === 'day'
              ? { key: 'group', direction: 'asc' }
              : { key: 'collected', direction: 'desc' }
          }
          emptyMessage={t('salesReport.noRows')}
          onRowClick={openRow}
        />
        {totals && totals.orders > 0 && (
          <p className="text-muted-foreground text-xs">
            {t('salesReport.rowsTotal', {
              collected: formatCurrency(totals.amountCollected),
              orders: formatNumber(totals.orders),
            })}
          </p>
        )}
      </section>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
        >
          {selected && (
            <>
              <SheetHeader className="border-b pr-12">
                <SheetTitle>
                  {rowLabel(selected)}
                  {selected.detail ? ` · ${selected.detail}` : ''}
                </SheetTitle>
                <SheetDescription>
                  {t('salesReport.ordersSubtitle', {
                    orders: formatNumber(selected.orders),
                    collected: formatCurrency(selected.amountCollected),
                  })}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-3 p-4">
                {orders.error && (
                  <p className="text-destructive text-sm">
                    {orders.error instanceof Error
                      ? orders.error.message
                      : t('salesReport.ordersFailed')}
                  </p>
                )}
                <OrderTable
                  orders={orders.data?.orders ?? []}
                  // `isLoading`: a row with no key leaves the query disabled,
                  // and pending forever.
                  loading={orders.isLoading}
                  busy={orders.isFetching}
                  pagination={{
                    page: ordersPage,
                    size: ORDERS_PAGE_SIZE,
                    total: orders.data?.total ?? selected.orders,
                    onPageChange: setOrdersPage,
                  }}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function OrderTable({
  orders,
  loading,
  busy,
  pagination,
}: {
  orders: SalesOrder[];
  loading: boolean;
  busy: boolean;
  pagination: {
    page: number;
    size: number;
    total: number;
    onPageChange: (page: number) => void;
  };
}) {
  const { t } = useTranslation();

  // Five columns, each a main line with a quieter one under it, rather than
  // eight flat ones — so an order fits the drawer without scrolling sideways.
  // Tender rides under the order code; refunds and gift card payments under
  // the amount, and only when there are any.
  const columns: SortableColumn<SalesOrder>[] = [
    {
      key: 'time',
      header: t('salesReport.time'),
      sortValue: (order) => order.dispatchedAt ?? '',
      render: (order) => (
        <span className="block whitespace-nowrap tabular-nums">
          <span className="block">
            {formatStoreDateTime(order.dispatchedAt, 'date')}
          </span>
          <span className="text-muted-foreground block text-xs">
            {formatStoreDateTime(order.dispatchedAt, 'time')}
          </span>
        </span>
      ),
    },
    {
      key: 'order',
      header: t('salesReport.order'),
      sortValue: (order) => order.confirmationCode ?? '',
      render: (order) => (
        <span className="block">
          <span className="block font-mono text-xs whitespace-nowrap">
            {order.confirmationCode || '—'}
          </span>
          {order.tender && (
            <span className="text-muted-foreground block text-xs">
              {order.tender}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'lane',
      header: t('salesReport.lane'),
      sortValue: (order) => `${order.laneTag ?? ''} ${order.businessName ?? ''}`,
      render: (order) => (
        <span className="block">
          <span className="block whitespace-nowrap">{order.laneTag || '—'}</span>
          {order.businessName && (
            <span className="text-muted-foreground block text-xs">
              {order.businessName}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'employee',
      header: t('salesReport.employee'),
      sortValue: (order) => order.employee ?? '',
      render: (order) => order.employee || '—',
    },
    {
      key: 'collected',
      header: t('dashboard.amountCollected'),
      align: 'right',
      sortValue: (order) => order.amountCollected,
      render: (order) => (
        <span className="block tabular-nums">
          <span className="block">{formatCurrency(order.amountCollected)}</span>
          {order.refunds > 0 && (
            <span className="text-muted-foreground block text-xs whitespace-nowrap">
              {t('salesReport.refundedAmount', {
                amount: formatCurrency(order.refunds),
              })}
            </span>
          )}
          {order.giftCardRedemptions > 0 && (
            <span className="text-muted-foreground block text-xs whitespace-nowrap">
              {t('salesReport.giftCardAmount', {
                amount: formatCurrency(order.giftCardRedemptions),
              })}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <SortableTable
      rows={orders}
      columns={columns}
      rowKey={(order) => order.id}
      loading={loading}
      busy={busy}
      pagination={pagination}
      initialSort={{ key: 'time', direction: 'desc' }}
      emptyMessage={t('salesReport.noOrders')}
    />
  );
}
